@if (isLoading) {
  <div class="grid grid-cols-2 gap-4">
    @for (skeleton of [1, 2]; track $index) {
      <div class="flex flex-col gap-2">
        <ngx-skeleton-loader
          class="h-[18px]"
          [theme]="{ width: '32px', height: '18px' }"
        ></ngx-skeleton-loader>

        <ngx-skeleton-loader
          class="h-10"
          [theme]="{ width: '100%', height: '40px', borderRadius: '6px' }"
        ></ngx-skeleton-loader>
      </div>
    }
  </div>
} @else {
  <div [class]="singleColumn() ? 'flex flex-col gap-4' : 'grid grid-cols-1 sm:grid-cols-2 gap-4'">
    @for (field of customFields()?.fields; track $index; let index = $index) {
      @let fieldGroup = formCustomFields?.controls?.fields?.controls?.[index];

      @if (fieldGroup) {
        @switch (field?.fieldTypeId) {
          @case (fieldTypes.Text) {
            <svc-text
              class="h-fit"
              fallbackTextValue="-"
              [inputId]="'form-custom-field-text' + index"
              [label]="field?.fieldName"
              [placeholder]="field?.mandatory ? '' : ('Opcional' | transloco)"
              [readonly]="isViewMode()"
              [formControl]="fieldGroup?.controls?.fieldValue"
              [maxLength]="fieldTypesMaxLength.Text"
            />
          }

          @case (fieldTypes.LongText) {
            <svc-text-area
              class="h-fit sm:col-span-2"
              fallbackTextValue="-"
              [inputId]="'form-custom-field-long-text' + index"
              [label]="field?.fieldName"
              [placeholder]="field?.mandatory ? '' : ('Opcional' | transloco)"
              [readonly]="isViewMode()"
              [formControl]="fieldGroup?.controls?.fieldValue"
              [maxLength]="fieldTypesMaxLength.LongText"
              [minRows]="3"
            />
          }

          @case (fieldTypes.Number) {
            <svc-text
              class="h-fit"
              mask="0*"
              fallbackTextValue="-"
              [inputId]="'form-custom-field-number' + index"
              [label]="field?.fieldName"
              [placeholder]="field?.mandatory ? '' : ('Opcional' | transloco)"
              [readonly]="isViewMode()"
              [formControl]="fieldGroup?.controls?.fieldValue"
              [maxLength]="fieldTypesMaxLength.Number"
            />
          }

          @case (fieldTypes.Date) {
            <!-- AB#76587 an empty date shows "-" like the other read-only fields -->
            <svc-date-picker
              class="h-fit"
              fallbackTextValue="-"
              [inputId]="'form-custom-field-date' + index"
              [formControl]="fieldGroup?.controls?.fieldValue"
              [label]="field?.fieldName"
              [readonly]="isViewMode()"
            />
          }

          @case (fieldTypes.List) {
            <svc-chip
              class="h-fit"
              type="single"
              fallbackTextValue="-"
              [inputId]="'form-custom-field-list' + index"
              [placeholder]="field?.mandatory ? '' : ('Opcional' | transloco)"
              [label]="field?.fieldName"
              [options]="field?.optionsCombo"
              [readonly]="isViewMode()"
              [formControl]="fieldGroup?.controls?.fieldValue"
            />
          }

          @case (fieldTypes.ListMultiple) {
            <svc-chip
              class="h-fit"
              type="multiple"
              fallbackTextValue="-"
              [inputId]="'form-custom-field-list-multiple' + index"
              [placeholder]="field?.mandatory ? '' : ('Opcional' | transloco)"
              [label]="field?.fieldName"
              [options]="field?.optionsCombo"
              [readonly]="isViewMode()"
              [formControl]="fieldGroup?.controls?.fieldValue"
            />
          }

          @case (fieldTypes.Checkbox) {
            @let checkboxControl = fieldGroup?.controls?.fieldValue;
            @let checkboxHasError = checkboxControl?.invalid && checkboxControl?.touched;
            <div class="flex flex-col h-full max-h-16 justify-end">
              <mat-checkbox
                color="primary"
                class="flex items-end -ml-2"
                svcTestId="form-custom-field__checkbox--{{ index }}"
                [id]="'form-custom-field-checkbox' + index"
                [disabled]="isViewMode()"
                [formControl]="checkboxControl"
                [attr.aria-invalid]="checkboxHasError"
                [attr.aria-describedby]="checkboxHasError ? 'form-custom-field-checkbox-error' + index : null"
              >
                {{ field?.fieldName }}
              </mat-checkbox>

              @if (checkboxHasError) {
                <span class="text-xs text-red-500" [id]="'form-custom-field-checkbox-error' + index">
                  {{ 'Campo obrigatório' | transloco }}
                </span>
              }
            </div>
          }

          @case (fieldTypes.Hour) {
            <svc-text
              class="h-fit"
              mask="Hh:m0"
              fallbackTextValue="-"
              [inputId]="'form-custom-field-hour' + index"
              [label]="field?.fieldName"
              [placeholder]="field?.mandatory ? '' : ('Opcional' | transloco)"
              [readonly]="isViewMode()"
              [formControl]="fieldGroup?.controls?.fieldValue"
              [dropSpecialCharacters]="false"
              tooltip="Por favor, insira a hora utilizando o padrão internacional de 24 horas"
              (onBlur)="formatHour(fieldGroup?.controls?.fieldValue)"
            />
          }

          @case (fieldTypes.User) {
            <svc-user-picker
              class="h-fit"
              fallbackTextValue="-"
              [inputId]="'form-custom-field-user' + index"
              [label]="field?.fieldName"
              [placeholder]="field?.mandatory ? '' : ('Opcional' | transloco)"
              [readonly]="isViewMode()"
              [formControl]="fieldGroup?.controls?.fieldValue"
              [loading]="isLoadingUsersIds"
              [ids]="usersIds"
            />
          }

          @case (fieldTypes.Location) {
            <svc-location-field
              class="h-fit"
              canSelectOnly="location"
              [label]="field?.fieldName"
              [placeholder]="field?.mandatory ? '' : ('Opcional' | transloco)"
              [inputId]="'form-custom-field-location' + index"
              [formControl]="fieldGroup?.controls?.fieldValue"
              [readonly]="isViewMode()"
            />
          }

          @case (fieldTypes.Area) {
            <svc-location-field
              class="h-fit"
              canSelectOnly="area"
              [label]="field?.fieldName"
              [placeholder]="field?.mandatory ? '' : ('Opcional' | transloco)"
              [inputId]="'form-custom-field-area' + index"
              [formControl]="fieldGroup?.controls?.fieldValue"
              [readonly]="isViewMode()"
            />
          }

          @case (fieldTypes.Team) {
            <svc-chip
              class="h-fit"
              type="multiple"
              fallbackTextValue="-"
              svcTestId="form-custom-field__chip--team-{{ index }}"
              [inputId]="'form-custom-field-team' + index"
              [placeholder]="field?.mandatory ? '' : ('Opcional' | transloco)"
              [label]="field?.fieldName"
              [options]="teamOptions"
              [loading]="isLoadingTeams"
              [readonly]="isViewMode()"
              [formControl]="fieldGroup?.controls?.fieldValue"
            />
          }

          @case (fieldTypes.Hashtag) {
            <svc-chip-grouped
              class="h-fit"
              type="multiple"
              fallbackTextValue="-"
              readonlyStyle="chip"
              svcTestId="form-custom-field__chip--hashtag-{{ index }}"
              [inputId]="'form-custom-field-hashtag' + index"
              [placeholder]="field?.mandatory ? '' : ('Opcional' | transloco)"
              [label]="field?.fieldName"
              [options]="hashtagOptions"
              [loading]="isLoadingHashtags"
              [collapsedDisplaying]="false"
              [readonly]="isViewMode()"
              [formControl]="fieldGroup?.controls?.fieldValue"
            />
          }

          @case (fieldTypes.Equipment) {
            <svc-select
              class="h-fit"
              fallbackTextValue="-"
              svcTestId="form-custom-field__select--equipment-{{ index }}"
              [inputId]="'form-custom-field-equipment' + index"
              [placeholder]="field?.mandatory ? '' : ('Opcional' | transloco)"
              [label]="field?.fieldName"
              [options]="equipmentOptions"
              [loading]="isLoadingEquipments"
              [readonly]="isViewMode()"
              [formControl]="fieldGroup?.controls?.fieldValue"
            />
          }
        }
      }
    }
  </div>
}
